import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import {
  Users, Building2, MapPin, TrendingUp, LogOut, Download, Search, Info,
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import USMap from "@/components/USMap";
import AssetManager from "@/components/AssetManager";
import EmailLog from "@/components/EmailLog";
import SiteEditor from "@/components/SiteEditor";
import LaunchDataReset from "@/components/LaunchDataReset";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS = ["New", "Reviewing", "Approved", "Waitlisted", "Rejected"];
const STATUS_COLORS = {
  New: "bg-brand/10 text-brand", Reviewing: "bg-amber-500/10 text-amber-600",
  Approved: "bg-ai/10 text-ai", Waitlisted: "bg-neutral-500/10 text-neutral-600",
  Rejected: "bg-sos/10 text-sos",
};

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-500">{label}</span>
        <Icon className="h-4 w-4 text-neutral-300" />
      </div>
      <p className="mt-2 text-3xl font-bold tracking-tight text-neutral-900">{value?.toLocaleString?.() ?? value}</p>
      {sub && <p className="mt-1 text-xs text-neutral-400">{sub}</p>}
    </div>
  );
}

const download = (path, name) => {
  const token = localStorage.getItem("sos_admin_token");
  fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob())
    .then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    });
};

export default function AdminDashboard() {
  const { admin, ready, logout } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [demand, setDemand] = useState(null);
  const [priority, setPriority] = useState(null);
  const [waitlist, setWaitlist] = useState({ rows: [], total: 0 });
  const [providers, setProviders] = useState({ rows: [], total: 0 });
  const [wSearch, setWSearch] = useState("");
  const [wType, setWType] = useState("all");
  const [pStatus, setPStatus] = useState("all");

  const loadOverview = () => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/launch-demand").then((r) => setDemand(r.data)).catch(() => {});
    api.get("/admin/launch-priority").then((r) => setPriority(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!admin) return;
    loadOverview();
    // eslint-disable-next-line
  }, [admin]);

  const loadWaitlist = () => api.get("/admin/waitlist", { params: { search: wSearch, user_type: wType, page_size: 50 } }).then((r) => setWaitlist(r.data)).catch(() => {});
  const loadProviders = () => api.get("/admin/providers", { params: { status: pStatus, page_size: 50 } }).then((r) => setProviders(r.data)).catch(() => {});

  useEffect(() => { if (admin) loadWaitlist(); /* eslint-disable-next-line */ }, [admin, wType]);
  useEffect(() => { if (admin) loadProviders(); /* eslint-disable-next-line */ }, [admin, pStatus]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/providers/${id}/status`, { status }); toast.success("Status updated"); loadProviders(); }
    catch { toast.error("Failed to update"); }
  };

  if (!ready) return <div className="grid min-h-screen place-items-center bg-[#FAFAFC] text-neutral-400">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-bold tracking-tight text-neutral-900">SOSConnect AI — Launch Command</h1>
            <p className="text-xs text-neutral-400">{admin.email}</p>
          </div>
          <Button variant="ghost" onClick={() => { logout(); nav("/admin/login"); }} className="gap-2" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sign out</Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <Tabs defaultValue="overview">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist">Waitlist</TabsTrigger>
            <TabsTrigger value="demand" data-testid="tab-demand">Launch Demand</TabsTrigger>
            <TabsTrigger value="priority" data-testid="tab-priority">Launch Priority</TabsTrigger>
            <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">Emails</TabsTrigger>
            <TabsTrigger value="assets" data-testid="tab-assets">Assets</TabsTrigger>
            <TabsTrigger value="site" data-testid="tab-site">Site Editor</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview">
            {overview && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Stat icon={Users} label="Total Waitlist" value={overview.total_waitlist} />
                <Stat icon={TrendingUp} label="New Today" value={overview.new_today} />
                <Stat icon={TrendingUp} label="New This Week" value={overview.new_week} />
                <Stat icon={Building2} label="Provider Applications" value={overview.total_providers} />
                <Stat icon={MapPin} label="Cities Requested" value={overview.cities_requested} />
                <Stat icon={MapPin} label="States Represented" value={overview.states_represented} />
                <Stat icon={Users} label="Customer Interest" value={overview.customer_interest} />
                <Stat icon={Users} label="Provider Interest" value={overview.provider_interest}
                  sub={`Ratio ${overview.customer_interest}:${overview.provider_interest}`} />
              </div>
            )}
            {demand && (
              <div className="mt-6 rounded-2xl border border-black/5 bg-white p-6">
                <p className="mb-4 text-sm font-semibold text-neutral-700">New signups over time</p>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={demand.over_time}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#0055FF" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <LaunchDataReset onDone={() => { loadOverview(); loadWaitlist(); loadProviders(); }} />
          </TabsContent>

          {/* WAITLIST */}
          <TabsContent value="waitlist">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <Input placeholder="Search email, city, state…" value={wSearch} onChange={(e) => setWSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadWaitlist()} className="h-11 rounded-xl pl-9" data-testid="waitlist-search" />
              </div>
              <Select value={wType} onValueChange={setWType}>
                <SelectTrigger className="h-11 w-40 rounded-xl" data-testid="waitlist-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="provider">Provider</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadWaitlist} className="h-11 rounded-xl bg-brand hover:bg-brand-dark" data-testid="waitlist-apply">Search</Button>
              <Button variant="outline" onClick={() => download("/admin/waitlist/export", "waitlist.csv")} className="h-11 gap-2 rounded-xl" data-testid="waitlist-export"><Download className="h-4 w-4" /> CSV</Button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-black/5 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead><TableHead>City</TableHead><TableHead>State</TableHead>
                    <TableHead>ZIP</TableHead><TableHead>Type</TableHead><TableHead>Source</TableHead><TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {waitlist.rows.map((r) => (
                    <TableRow key={r.id} data-testid="waitlist-tr">
                      <TableCell className="font-medium">{r.email}</TableCell>
                      <TableCell>{r.city}</TableCell><TableCell>{r.state}</TableCell>
                      <TableCell>{r.zip_code || "—"}</TableCell>
                      <TableCell><Badge variant="secondary" className="capitalize">{r.user_type}</Badge></TableCell>
                      <TableCell className="capitalize text-neutral-500">{r.source}</TableCell>
                      <TableCell className="text-neutral-500">{r.created_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="mt-3 text-sm text-neutral-400">{waitlist.total} total signups</p>
          </TabsContent>

          {/* DEMAND */}
          <TabsContent value="demand">
            {demand && (
              <div className="space-y-6">
                <div className="rounded-2xl border border-black/5 bg-white p-4 sm:p-6">
                  <p className="mb-2 text-sm font-semibold text-neutral-700">Signup demand map</p>
                  <USMap markers={demand.by_city.filter((c) => c.lat && c.lng).map((c) => ({ ...c }))} />
                </div>
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl border border-black/5 bg-white p-6">
                    <p className="mb-4 text-sm font-semibold text-neutral-700">Top requested cities</p>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={demand.by_city.slice(0, 8)} layout="vertical" margin={{ left: 30 }}>
                        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={90} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#0055FF" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="rounded-2xl border border-black/5 bg-white p-6">
                    <p className="mb-4 text-sm font-semibold text-neutral-700">Signups by state</p>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={demand.by_state.slice(0, 8)}>
                        <XAxis dataKey="state" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#34C759" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="overflow-hidden rounded-2xl border border-black/5 bg-white">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>City</TableHead><TableHead>Total</TableHead><TableHead>Customers</TableHead>
                      <TableHead>Providers (waitlist)</TableHead><TableHead>Businesses applied</TableHead><TableHead>C:P ratio</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {demand.by_city.slice(0, 20).map((c) => (
                        <TableRow key={c.label}>
                          <TableCell className="font-medium">{c.label}</TableCell>
                          <TableCell>{c.count}</TableCell><TableCell>{c.customers}</TableCell>
                          <TableCell>{c.providers}</TableCell><TableCell>{c.business_providers}</TableCell>
                          <TableCell>{c.providers ? (c.customers / c.providers).toFixed(1) : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* PRIORITY */}
          <TabsContent value="priority">
            {priority && (
              <>
                <div className="mb-5 flex items-start gap-3 rounded-2xl border border-brand/15 bg-brand/5 p-5 text-sm text-neutral-600">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <div>
                    <p className="font-semibold text-neutral-800">How the Launch Priority Score works (out of 100)</p>
                    <ul className="mt-1 space-y-0.5">
                      {Object.values(priority.formula).map((f) => <li key={f}>• {f}</li>)}
                    </ul>
                  </div>
                </div>
                <div className="overflow-hidden rounded-2xl border border-black/5 bg-white">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>#</TableHead><TableHead>City</TableHead><TableHead>Total</TableHead>
                      <TableHead>Customers</TableHead><TableHead>Providers</TableHead><TableHead>Recent (7d)</TableHead><TableHead>Launch Score</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {priority.cities.map((c, i) => (
                        <TableRow key={c.label} data-testid="priority-row">
                          <TableCell className="text-neutral-400">{i + 1}</TableCell>
                          <TableCell className="font-semibold">{c.label}</TableCell>
                          <TableCell>{c.total}</TableCell><TableCell>{c.customers}</TableCell>
                          <TableCell>{c.providers}</TableCell><TableCell>{c.recent}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-20 overflow-hidden rounded-full bg-black/5"><div className="h-full rounded-full bg-brand" style={{ width: `${c.score}%` }} /></div>
                              <span className="font-bold text-neutral-900">{c.score}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </TabsContent>

          {/* PROVIDERS */}
          <TabsContent value="providers">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Select value={pStatus} onValueChange={setPStatus}>
                <SelectTrigger className="h-11 w-44 rounded-xl" data-testid="provider-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => download("/admin/providers/export", "providers.csv")} className="h-11 gap-2 rounded-xl" data-testid="provider-export"><Download className="h-4 w-4" /> CSV</Button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-black/5 bg-white">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Business</TableHead><TableHead>Contact</TableHead><TableHead>Location</TableHead>
                  <TableHead>Categories</TableHead><TableHead>Applied</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {providers.rows.map((p) => (
                    <TableRow key={p.id} data-testid="provider-tr">
                      <TableCell><p className="font-semibold">{p.business_name}</p><p className="text-xs text-neutral-400">{p.email}</p></TableCell>
                      <TableCell>{p.contact_name}<p className="text-xs text-neutral-400">{p.phone}</p></TableCell>
                      <TableCell>{p.city}, {p.state}</TableCell>
                      <TableCell className="max-w-[180px] text-xs text-neutral-500">{(p.service_categories || []).join(", ")}</TableCell>
                      <TableCell className="text-neutral-500">{p.created_at?.slice(0, 10)}</TableCell>
                      <TableCell>
                        <Select value={p.status} onValueChange={(v) => setStatus(p.id, v)}>
                          <SelectTrigger className={`h-8 w-32 rounded-lg border-0 text-xs font-semibold ${STATUS_COLORS[p.status]}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="mt-3 text-sm text-neutral-400">{providers.total} total applications</p>
          </TabsContent>

          {/* EMAILS */}
          <TabsContent value="emails">
            <EmailLog />
          </TabsContent>

          {/* ASSETS */}
          <TabsContent value="assets">
            <AssetManager />
          </TabsContent>

          {/* SITE EDITOR */}
          <TabsContent value="site">
            <SiteEditor />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
